import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import moment from "moment";
import { tokens } from "../../../theme";

const formatDate = (dateVal) => {
    if (!dateVal && dateVal !== 0) return "-";
    if (typeof dateVal === "number" || (!isNaN(Number(dateVal)) && String(dateVal).length <= 11)) {
        return moment.unix(Number(dateVal)).format("DD-MM-YYYY");
    }
    const m = moment(dateVal);
    if (m.isValid()) {
        return m.format("DD-MM-YYYY");
    }
    return String(dateVal);
};

const formatRate = (rate) => {
    if (rate === undefined || rate === null || rate === "") return "-";
    const num = Number(rate);
    if (isNaN(num)) return "-";
    return `₹${num.toLocaleString("en-IN")}`;
};

function RatesPreviewModal({ open, onClose, ratesData, offerContext }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedRoomType, setSelectedRoomType] = useState("ALL");

    // Extract rates list
    const ratesList = useMemo(() => {
        if (!ratesData) return [];
        if (Array.isArray(ratesData)) return ratesData;
        if (Array.isArray(ratesData.data)) return ratesData.data;
        return [];
    }, [ratesData]);

    // Extract room types for filtering
    const roomTypes = useMemo(() => {
        if (ratesData?.roomtypes?.length) {
            return ratesData.roomtypes;
        }
        const map = new Map();
        ratesList.forEach((r) => {
            const rt = r?.roomtype_id || r?.roomtype;
            if (rt?._id && !map.has(rt._id)) {
                map.set(rt._id, rt);
            }
        });
        return Array.from(map.values());
    }, [ratesData, ratesList]);

    // Filtered by selected room type
    const displayedRows = useMemo(() => {
        if (selectedRoomType === "ALL") return ratesList;
        return ratesList.filter(
            (r) => (r.roomtype_id?._id || r.roomtype?._id) === selectedRoomType
        );
    }, [ratesList, selectedRoomType]);

    const isIncrease = offerContext?.plusPercentage > 0 || offerContext?.plusOrMinus === "plus";
    const adjustmentPct = isIncrease
        ? (offerContext?.plusPercentage || 0)
        : (offerContext?.minusPercentage || 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                zIndex: 13000,
            }}
            style={{
                zIndex: 13000,
            }}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    bgcolor: colors.primary[400],
                    backgroundImage: "none",
                },
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ p: 2, pb: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                            Rates Preview
                        </Typography>
                        {offerContext && (
                            <Typography variant="body2" color={colors.grey[300]} sx={{ mt: 0.5 }}>
                                {offerContext.startDate && offerContext.endDate ? (
                                    <>
                                        {formatDate(offerContext.startDate)} to {formatDate(offerContext.endDate)}
                                    </>
                                ) : null}
                                {offerContext.percentage !== undefined && (
                                    <> • Occupancy: {offerContext.percentage}% - {offerContext.prevPercentage ?? 100}%</>
                                )}
                                {adjustmentPct > 0 && (
                                    <> • Rate: {isIncrease ? `+${adjustmentPct}%` : `-${adjustmentPct}%`}</>
                                )}
                            </Typography>
                        )}
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: colors.grey[200] }}>
                        <Close fontSize="small" />
                    </IconButton>
                </Stack>

                {/* Room Type Filter Pills */}
                {roomTypes.length > 1 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.8, mt: 1.5 }}>
                        <Chip
                            label="All Room Types"
                            size="small"
                            onClick={() => setSelectedRoomType("ALL")}
                            sx={{
                                cursor: "pointer",
                                bgcolor: selectedRoomType === "ALL" ? colors.greenAccent[600] : colors.primary[500],
                                color: selectedRoomType === "ALL" ? "#fff" : colors.grey[200],
                                fontWeight: selectedRoomType === "ALL" ? "bold" : "normal",
                            }}
                        />
                        {roomTypes.map((rt) => (
                            <Chip
                                key={rt._id}
                                label={rt.name || "Unnamed"}
                                size="small"
                                onClick={() => setSelectedRoomType(rt._id)}
                                sx={{
                                    cursor: "pointer",
                                    bgcolor: selectedRoomType === rt._id ? colors.greenAccent[600] : colors.primary[500],
                                    color: selectedRoomType === rt._id ? "#fff" : colors.grey[200],
                                    fontWeight: selectedRoomType === rt._id ? "bold" : "normal",
                                }}
                            />
                        ))}
                    </Stack>
                )}
            </DialogTitle>

            {/* Table Content */}
            <DialogContent sx={{ p: 2, pt: 0 }}>
                <TableContainer
                    component={Paper}
                    sx={{
                        maxHeight: "60vh",
                        bgcolor: colors.primary[500],
                        borderRadius: 1,
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: colors.primary[400], fontWeight: "bold" }}>Date</TableCell>
                                <TableCell sx={{ bgcolor: colors.primary[400], fontWeight: "bold" }}>Room Type</TableCell>
                                <TableCell align="right" sx={{ bgcolor: colors.primary[400], fontWeight: "bold" }}>
                                    Double Rate
                                </TableCell>
                                <TableCell align="right" sx={{ bgcolor: colors.primary[400], fontWeight: "bold" }}>
                                    Single Rate
                                </TableCell>
                                <TableCell align="right" sx={{ bgcolor: colors.primary[400], fontWeight: "bold" }}>
                                    Triple Rate
                                </TableCell>
                                <TableCell align="right" sx={{ bgcolor: colors.primary[400], fontWeight: "bold" }}>
                                    Extra Bed
                                </TableCell>
                                <TableCell align="right" sx={{ bgcolor: colors.primary[400], fontWeight: "bold" }}>
                                    Child Rate
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedRows.length > 0 ? (
                                displayedRows.map((row, idx) => (
                                    <TableRow
                                        key={idx}
                                        sx={{
                                            "&:last-child td, &:last-child th": { border: 0 },
                                            "&:hover": { bgcolor: colors.primary[400] },
                                        }}
                                    >
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {formatDate(row.date)}
                                        </TableCell>
                                        <TableCell>
                                            {row.roomtype_id?.name || row.roomtype?.name || "-"}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold", color: colors.greenAccent[400] }}>
                                            {formatRate(row.standard_rate)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatRate(row.single_occupancy_rate)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatRate(row.max_occupancy_rate)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatRate(row.extra_bed_rate)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatRate(row.child_rate)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography color={colors.grey[300]}>No rates found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            {/* Footer */}
            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button variant="contained" size="small" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default RatesPreviewModal;
